import { useEffect, useState } from "react";
import { SEO } from "../components/SEO";
import { apiFetch } from "../lib/api";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  publishedAt: string;
  readTime: number;
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/blog")
      .then((json) => {
        if (json.success) {
          setPosts(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title="Blog"
        description="Novidades, cases de sucesso e dicas sobre o ZWeb. Fique por dentro das últimas tendências em gestão empresarial e ERP."
        keywords={["blog", "zweb", "erp", "gestão", "novidades", "dicas"]}
        canonicalUrl="/blog"
      />
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/faq" className="text-sm font-medium hover:text-accent-600">FAQ</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <h1 className="text-4xl font-bold">Blog</h1>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              Novidades, cases de sucesso e dicas sobre o ZWeb.
            </p>
          </div>

          {loading ? (
            <div className="mt-12 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
            </div>
          ) : posts.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                Em breve publicaremos novos artigos!
              </p>
            </div>
          ) : (
            <div className="mt-12 grid gap-8 md:grid-cols-2">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 dark:bg-accent-900 dark:text-accent-300">
                      {post.category}
                    </span>
                    <span className="text-sm text-gray-500">{post.readTime} min de leitura</span>
                  </div>
                  <h2 className="mt-4 text-xl font-semibold">
                    <a href={`/blog/${post.slug}`} className="hover:text-accent-600">
                      {post.title}
                    </a>
                  </h2>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                    {post.excerpt}
                  </p>
                  <div className="mt-4 text-sm text-gray-500">
                    {new Date(post.publishedAt).toLocaleDateString("pt-BR")}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}